import { useEffect, useState } from "react";
import useSeo from "@/seo/useSeo";
import { Link } from "react-router-dom";
import { Users, BedDouble, Bath, MapPin, Bike, Footprints } from "lucide-react";
import { api } from "@/lib/api";
import Reveal from "@/components/Reveal";
import { useI18n } from "@/i18n";
import { photoAlt, coverAlt } from "@/i18n/photoAlts";

const COVER =
  "https://customer-assets-m6fa6gv7.emergentagent.net/job_gite-reserve-now/artifacts/rdd4198l_IMG_8267.jpeg";


const BikeIcon = ({ className }) => <Bike className={className} strokeWidth={1.4} />;
const HikeIcon = ({ className }) => <Footprints className={className} strokeWidth={1.4} />;

const SkisIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4"
    strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
    <path d="M6 3.5 L11 19.5" />
    <path d="M10.5 3.5 L15.5 19.5" />
    <path d="M9.5 20.5c1 .8 2.2.8 3.2 0" />
    <path d="M14 20.5c1 .8 2.2.8 3.2 0" />
    <path d="M18.5 4v14" />
    <path d="M16.8 4.6h3.4" />
  </svg>
);

const SnowshoeIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.3"
    strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
    <g transform="rotate(-22 12 12)">
      <path d="M9 2.6c2.2 0 3.6 2.6 3.6 6.3 0 3.8-1.4 8.5-3.6 8.5S5.4 12.7 5.4 8.9C5.4 5.2 6.8 2.6 9 2.6Z" />
      <path d="M5.8 7.3h6.4M5.5 10.6h7M6.1 13.9h5.8" />
    </g>
    <g transform="rotate(22 12 12)">
      <path d="M15 6.6c2.2 0 3.6 2.6 3.6 6.3 0 3.8-1.4 8.5-3.6 8.5s-3.6-4.7-3.6-8.5c0-3.7 1.4-6.3 3.6-6.3Z" />
      <path d="M11.8 11.3h6.4M11.5 14.6h7M12.1 17.9h5.8" />
    </g>
  </svg>
);

const MuseumIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4"
    strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
    <path d="M3 9.5 12 4l9 5.5" />
    <path d="M4.5 9.5v9M9 12v6.5M15 12v6.5M19.5 9.5v9" />
    <path d="M3 21h18" />
  </svg>
);

const AbsintheIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4"
    strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
    <path d="M10.5 2.5h3v3.2c0 .8.3 1.6.9 2.2l1 1a4 4 0 0 1 1.1 2.8v7.3a2 2 0 0 1-2 2H9.5a2 2 0 0 1-2-2v-7.3a4 4 0 0 1 1.1-2.8l1-1c.6-.6.9-1.4.9-2.2V2.5Z" />
    <path d="M7.6 13.5h8.8" />
  </svg>
);

const GiteCard = ({ g, i }) => {
  const { t, lang } = useI18n();
  return (
    <Reveal delay={i * 110} className="group" data-testid={`gite-card-${g.slug}`}>
      <Link to={`/gite/${g.slug}`} className="block">
        <div className="img-hover relative aspect-square w-full overflow-hidden bg-ink">
          <img
            src={g.image}
            alt={photoAlt(lang, g.image, 0)}
            className="h-full w-full object-cover"
            style={{ objectFit: g.images.length ? "cover" : "contain", filter: g.images.length ? "brightness(1.14) contrast(1.06)" : "none" }}
          />
          {!g.images.length && (
            <span className="absolute inset-x-0 bottom-0 bg-ink/80 py-3 text-center text-[10px] uppercase tracking-[0.3em] text-brass">
              {t("nav.soon")}
            </span>
          )}
        </div>
        <div className="mt-6">
          <p className="mono-label">0{i + 1}</p>
          <h3 className="mt-3 font-display text-4xl text-bone">
            {g.slug === "ensemble-10" ? t("home.rentBoth") : g.name}
          </h3>
          <p className="mt-2 text-[11px] uppercase tracking-[0.2em] text-brass">{g.subtitle}</p>
          <div className="mt-5 flex flex-wrap gap-x-6 gap-y-2 text-[10px] uppercase tracking-[0.18em] text-bone/72">
            <span className="flex items-center gap-2"><Users size={12} /> {g.capacity} {t("unit.pers")}</span>
            <span className="flex items-center gap-2"><BedDouble size={12} /> {g.bedrooms} {t("unit.rooms")}</span>
            <span className="flex items-center gap-2"><Bath size={12} /> {g.baths} {t("unit.baths")}</span>
          </div>
          <p className="mt-5 text-sm leading-relaxed text-bone/70">{g.short}</p>
          <span className="link-underline mt-6 inline-block text-[11px] uppercase tracking-[0.22em] text-bone/80" data-testid={`details-${g.slug}`}>
            {g.active ? t("home.details") : t("home.detailsSoon")}
          </span>
        </div>
      </Link>
    </Reveal>
  );
};

export default function Home() {
  const [gites, setGites] = useState([]);
  const { t, tg, lang } = useI18n();
  useSeo("/");

  useEffect(() => {
    api.get("/gites").then((r) => setGites(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* COUVERTURE */}
      <section className="relative w-full overflow-hidden bg-ink">
        <img
          src={COVER}
          alt={coverAlt(lang)}
          className="mx-auto block h-auto w-full max-w-[1600px] object-contain"
          style={{ filter: "brightness(1.24) contrast(1.1) saturate(1.05)" }}
        />
      </section>

      {/* LA MAISON */}
      <section className="border-t border-white/10">
        <div className="mx-auto max-w-[1600px] px-6 py-24 sm:px-10">
          <div className="grid gap-16 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <p className="eyebrow">{t("home.house")}</p>
              <h1 className="display-xl mt-8 text-5xl text-bone sm:text-6xl">{t("home.title")}</h1>
              <p className="mono-label mt-8 flex items-center gap-2">
                <MapPin size={13} className="text-brass" /> Les Fins · 25500 · Franche-Comté
              </p>
            </div>
            <div className="lg:col-span-7">
              <Reveal>
                <p className="text-lg leading-loose text-bone/75">{t("home.p1")}</p>
              </Reveal>
              <Reveal delay={120}>
                <p className="mt-8 text-lg leading-loose text-bone/75">
                  {t("home.p2a")}<span className="text-bone">{t("home.p2b")}</span>{t("home.p2c")}
                </p>
              </Reveal>
              <Reveal delay={200}>
                <div className="mt-14 border-y border-white/10 py-10">
                  <p className="mono-label">{t("home.activities")}</p>
                  <div className="mt-8 grid grid-cols-2 gap-y-10 sm:grid-cols-3">
                    {[["act.bike", BikeIcon], ["act.ski", SkisIcon], ["act.snowshoe", SnowshoeIcon],
                      ["act.hike", HikeIcon], ["act.museum", MuseumIcon],
                      ["act.absinthe", AbsintheIcon]].map(([k, Icon]) => (
                      <div key={k} className="px-2 text-center">
                        <p className="display-xl text-2xl text-bone sm:text-3xl">{t(k)}</p>
                        <Icon className="mx-auto mt-4 h-7 w-7 text-brass" />
                      </div>
                    ))}
                  </div>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* NOS APPARTEMENTS */}
      <section className="border-t border-white/10">
        <div className="mx-auto max-w-[1600px] px-6 py-24 sm:px-10">
          <p className="eyebrow">{t("home.lodgings")}</p>
          <div className="mt-16 grid gap-12 md:grid-cols-3" data-testid="gites-grid">
            {gites.map(tg).map((g, i) => <GiteCard key={g.slug} g={g} i={i} />)}
          </div>
        </div>
      </section>
    </div>
  );
}
